import { DEFAULT_UPLOAD_CONCURRENCY } from '@/consts/upload'
import { TaskResult } from '@/utils/concurrent-pool'
import {
  BaseChunkUploader,
  ChunkUploadResponse,
  DEFAULT_CHUNK_SIZE,
  UploadPausedError,
} from '@/utils/upload'

export const CHUNK_SIZE = DEFAULT_CHUNK_SIZE

// Response type with blkWritten support
export interface UploadPartResponse extends ChunkUploadResponse {
  code: number
}

// Context for file chunk uploads
interface FileUploadContext {
  md5: string
  file: File
}

export interface IFileUploaderClientOptions {
  chunkSize: number
  concurrency?: number
  requestOptions?: {
    retryTimes: number
    retryDelays?: number[]
    initFilePartUploadFunc: () => Promise<any>
    uploadPartFileFunc: (options: {
      chunk: Blob
      index: number
      fileMd5: string
      fileName: string
      fileSize: number
      signal?: AbortSignal
    }) => Promise<UploadPartResponse>
    finishFilePartUploadFunc: (md5: string) => Promise<any>
  }
}

export class FileUploaderClient extends BaseChunkUploader {
  private fileUploaderClientOptions: IFileUploaderClientOptions
  private requestOptions!: NonNullable<IFileUploaderClientOptions['requestOptions']>

  // Pause state for resume support
  private pausedState?: {
    file: File
    md5: string
    chunkList: Blob[]
    completedChunks: Set<number>
  }

  constructor(options: IFileUploaderClientOptions) {
    super({
      chunkSize: options.chunkSize ?? DEFAULT_CHUNK_SIZE,
      concurrency: options.concurrency ?? DEFAULT_UPLOAD_CONCURRENCY,
      retryTimes: options.requestOptions?.retryTimes,
      retryDelays: options.requestOptions?.retryDelays,
    })
    this.fileUploaderClientOptions = options
  }

  /**
   * Upload file with parallel chunk uploads and blkWritten support
   * @param file File to upload
   * @returns Result from finishFilePartUploadFunc
   */
  public async uploadFile(file: File): Promise<any> {
    const requestOptions = this.fileUploaderClientOptions.requestOptions

    if (
      requestOptions?.retryTimes === undefined ||
      !requestOptions?.initFilePartUploadFunc ||
      !requestOptions?.uploadPartFileFunc ||
      !requestOptions?.finishFilePartUploadFunc
    ) {
      throw Error(
        'invalid request options, need retryTimes, initFilePartUploadFunc, uploadPartFileFunc and finishFilePartUploadFunc',
      )
    }

    this.requestOptions = requestOptions

    // Reset state for new upload
    this.resetState()

    const { md5, chunkList } = await this.getChunkListAndFileMd5(file)
    const context: FileUploadContext = { md5, file }
    const uploadFn = this.createUploadFn()

    // Initialize upload
    await requestOptions.initFilePartUploadFunc()
    if (this.isCanceled()) throw new Error('canceled')
    if (this.isPaused()) {
      this.pausedState = { file, md5, chunkList, completedChunks: new Set() }
      throw new UploadPausedError()
    }

    // For single chunk files, just upload directly
    if (chunkList.length === 1) {
      await this.uploadSingleChunkWithRetry(chunkList[0], 0, context, uploadFn)
      return await requestOptions.finishFilePartUploadFunc(md5)
    }

    // Step 1: Upload chunk 0 to probe blkWritten
    const firstResponse = await this.uploadSingleChunkWithRetry(chunkList[0], 0, context, uploadFn)
    if (this.isCanceled()) throw new Error('canceled')
    if (this.isPaused()) {
      this.pausedState = { file, md5, chunkList, completedChunks: new Set([0]) }
      throw new UploadPausedError()
    }

    const blkWritten = firstResponse?.info?.blkWritten || {}

    // Step 2: Filter chunks that need uploading (skip already uploaded)
    const chunksToUpload: number[] = []
    for (let i = 1; i < chunkList.length; i++) {
      if (!blkWritten[i.toString()]) {
        chunksToUpload.push(i)
      }
    }

    // If all chunks already uploaded, finish
    if (chunksToUpload.length === 0) {
      return await requestOptions.finishFilePartUploadFunc(md5)
    }

    // Step 3: Upload remaining chunks in parallel
    const pool = this.createPool<UploadPartResponse>()

    const tasks = chunksToUpload.map((index) => ({
      index,
      task: () => this.uploadSingleChunkWithRetry(chunkList[index], index, context, uploadFn),
    }))

    const results = await pool.execute(tasks)

    if (this.isCanceled()) throw new Error('canceled')
    if (this.isPaused()) {
      // Save completed chunks for resume
      const completedChunks = new Set([0]) // chunk 0 already uploaded
      results.forEach((r) => {
        if (r.success) completedChunks.add(r.index)
      })
      this.pausedState = { file, md5, chunkList, completedChunks }
      throw new UploadPausedError()
    }

    // Step 4: Check for task execution failures
    const failed = results.filter((r: TaskResult<UploadPartResponse>) => !r.success)
    if (failed.length > 0) {
      const failedIndices = failed.map((f: TaskResult<UploadPartResponse>) => f.index).join(', ')
      const errors = failed
        .map(
          (f: TaskResult<UploadPartResponse>) =>
            `chunk ${f.index}: ${f.error?.message || 'unknown error'}`,
        )
        .join('\n')
      throw new Error(`Failed to upload chunks [${failedIndices}]:\n${errors}`)
    }

    // Step 5: Verify blkWritten and retry failed chunks
    await this.verifyAndRetryFailedChunks(
      chunkList,
      context,
      [{ index: 0, success: true, result: firstResponse }, ...results],
      pool,
      uploadFn,
    )

    // Step 6: Finish upload
    return await requestOptions.finishFilePartUploadFunc(md5)
  }

  /**
   * Resume a paused upload
   * @returns Result from finishFilePartUploadFunc
   */
  public async resumeUpload(): Promise<any> {
    if (!this.pausedState) {
      throw new Error('No paused upload to resume')
    }

    this.resume()
    const { file, md5, chunkList, completedChunks } = this.pausedState
    const context: FileUploadContext = { md5, file }
    const uploadFn = this.createUploadFn()

    // Find remaining chunks to upload
    const remainingChunks: number[] = []
    for (let i = 0; i < chunkList.length; i++) {
      if (!completedChunks.has(i)) {
        remainingChunks.push(i)
      }
    }

    // If all chunks completed, finish
    if (remainingChunks.length === 0) {
      this.pausedState = undefined
      return await this.requestOptions.finishFilePartUploadFunc(md5)
    }

    // Upload remaining chunks
    const pool = this.createPool<UploadPartResponse>()

    const tasks = remainingChunks.map((index) => ({
      index,
      task: () => this.uploadSingleChunkWithRetry(chunkList[index], index, context, uploadFn),
    }))

    const results = await pool.execute(tasks)

    if (this.isCanceled()) {
      this.pausedState = undefined
      throw new Error('canceled')
    }

    if (this.isPaused()) {
      // Update completed chunks
      results.forEach((r) => {
        if (r.success) completedChunks.add(r.index)
      })
      this.pausedState = { file, md5, chunkList, completedChunks }
      throw new UploadPausedError()
    }

    // Check for failures
    const failed = results.filter((r) => !r.success)
    if (failed.length > 0) {
      const failedIndices = failed.map((f) => f.index).join(', ')
      throw new Error(`Failed to upload chunks [${failedIndices}]`)
    }

    // Verify blkWritten and retry failed chunks
    await this.verifyAndRetryFailedChunks(chunkList, context, results, pool, uploadFn)

    // Clear paused state and finish
    this.pausedState = undefined
    return await this.requestOptions.finishFilePartUploadFunc(md5)
  }

  /**
   * Check if there is a paused upload that can be resumed
   */
  public hasPausedUpload(): boolean {
    return this.pausedState !== undefined
  }

  /**
   * Create upload function that uses requestOptions
   */
  private createUploadFn() {
    return async (
      chunk: Blob,
      index: number,
      context: FileUploadContext,
      signal: AbortSignal,
    ): Promise<UploadPartResponse> => {
      return this.requestOptions.uploadPartFileFunc({
        chunk,
        index,
        fileMd5: context.md5,
        fileName: context.file.name,
        fileSize: context.file.size,
        signal,
      })
    }
  }
}
